import { action, observable } from 'mobx';
import { isBoolean, isString } from '~/common/utils/typeCheckers.js';
import { abandonnmentEmail } from "~/common/services/sitecoreServices";

export default class FormsClass {
    constructor(rootStore) {
        this.rootStore = rootStore;
    }

    @observable FirstName = '';
    @observable FirstNameError = '';
    @observable FirstNameValid = false;
    @observable LastName = '';
    @observable LastNameError = '';
    @observable LastNameValid = false;
    @observable Email = '';
    @observable EmailChange = '';
    @observable EmailError = '';
    @observable EmailValid = false;
    @observable PhoneNumber = '';
    @observable PhoneNumberError = '';
    @observable PhoneNumberValid = false;
    @observable JobTitle = '';
    @observable JobTitleError = '';
    @observable JobTitleValid = false;
    @observable Password = '';
    @observable PasswordError = '';
    @observable PasswordValid = false;
    @observable PasswordFocus = false;
    @observable displayOptIn = false;
    @observable IsNewsletter = true;
    @observable Requirement1 = false;
    @observable Requirement2 = false;
    @observable Requirement3 = false;
    @observable RadioError = '';
    @observable CompanySize = '';
    @observable webinar = false;
    @observable room = false;
    @observable openvoice = false;
    @observable connect = false;

    // setters
    // set all form values
    @action setFormData(input, val) {
        if (typeof input === 'string' && (typeof val === 'string' || typeof val === 'boolean')) {
            this[input] = val;
        }
    }

    // newsletter
    @action toggleNewsletter() {
        this.IsNewsletter = !this.IsNewsletter;
    }

    // remove error from input
    @action clearFieldError(name) {
        if (typeof name === 'string') {
            this[`${name}Error`] = '';
        }
    }

    @action setFieldError(name, error) {
        if (typeof name === 'string' && typeof error === 'string') {
            this[`${name}Error`] = error;
        }
    }

    // set field warning
    @action setFieldWarning(name, warning) {
        if (typeof name === 'string' && typeof warning === 'string') {
            this[`${name}Warning`] = warning;
        }
    }

    @action setCompanySize(e) {
        this.CompanySize = isString(e.target.value, this.CompanySize);
        this.RadioValidation();
    }

    @action removeFieldWarning(name) {
        if (typeof name === 'string') {
            this[`${name}Warning`] = '';
        }
    }

    @action removeValidationErrors(fields) {
        fields.forEach((field) => {
            this.clearFieldError(field);
        });
    }

    @action validateForm(fields) {
        this.removeValidationErrors(fields);
        fields.forEach((field) => {
            this[`${field}Validation`]();
        });
    }

    @action doesFormHaveErrors(fields) {
        return fields.find((field) => {
            return this[`${field}Error`].length > 0;
        });
    }

    @action areFieldsEmpty(fields) {
        return !!fields.find((field) => {
            return this[`${field}`].length === 0;
        });
    }

    // escapes characters in a string, so regExp works
    @action escapeRegExp(text) {
        return text ? text.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&') : '';
    }

    // form valiation
    @action isFieldEmpty(input) {
        return this[input] === '';
    }

    @action setDisplayOptIn(e, bool) {
        e.preventDefault();
        this.displayOptIn = isBoolean(bool, this.displayOptIn);
    }

    @action setPasswordFocus(bool) {
        this.PasswordFocus = isBoolean(bool, this.PasswordFocus);
    }

    // newsletter
    @action toggleNewsletter() {
        this.IsNewsletter = !this.IsNewsletter;
    }

    @action onlyNumbers(input, allowedChar = '') {
        const allowed = this.escapeRegExp(allowedChar);
        const regex = new RegExp(`[^0-9${allowed}]+`, 'g');
        if (input !== '') {
            return regex.test(this[input]);
        }
        return true;
    }

    @action onlyLetters(input, allowedChar = '') {
        const allowed = this.escapeRegExp(allowedChar);
        const regex = new RegExp(`[^a-zA-Z${allowed}]+`, 'g');
        if (input !== '') {
            return regex.test(this[input]);
        }
        return true;
    }

    @action onlyLatin(input, allowedChar = '') {
        const allowed = this.escapeRegExp(allowedChar);
        const regex = new RegExp(`[^A-zÀ-ÖØ-öø-ÿ${allowed}]+`, 'g');
        if (input !== '') {
            return regex.test(this[input]);
        }
        return true;
    }

    @action onlyNumbersAndLetters(input, allowedChar = '') {
        const allowed = this.escapeRegExp(allowedChar);
        const regex = new RegExp(`[^a-zA-Z0-9${allowed}]+`, 'g');
        if (input !== '') {
            return regex.test(this[input]);
        }
        return true;
    }

    @action hasInvalidCharacter(input, disallowedChar = '') {
        const disallowed = this.escapeRegExp(disallowedChar);
        const regex = new RegExp(`[${disallowed}]+`, 'g');
        if (input !== '') {
            return regex.test(this[input]);
        }
        return true;
    }

    @action isInputNotSelected(input) {
        return this[input] === '' || this[input] === false;
    }

    @action isRequired(field) {
        return `${field} ${this.billingInfoData.ErrorRequired}`;
    }

    // error validation for specific fields
    @action FirstNameValidation() {
        let FirstNameError = '';
        this.FirstNameValid = false;
        if (this.isFieldEmpty('FirstName')) {
            FirstNameError = this.rootStore.trialStore.trialData.ErrorFirstName;
        } else if (this.onlyNumbersAndLetters('FirstName', " -'")) {
            FirstNameError = this.rootStore.trialStore.trialData.ErrorInvalidCharacter;
        } else {
            this.FirstNameValid = true;
        }
        this.FirstNameError = FirstNameError;
    }

    @action LastNameValidation() {
        let LastNameError = '';
        this.LastNameValid = false;
        if (this.isFieldEmpty('LastName')) {
            LastNameError = this.rootStore.trialStore.trialData.ErrorLastName;
        } else if (this.onlyNumbersAndLetters('LastName', " -'")) {
            LastNameError = this.rootStore.trialStore.trialData.ErrorInvalidCharacter;
        } else {
            this.LastNameValid = true;
        }
        this.LastNameError = LastNameError;
    }

    @action EmailValidation() {
        let EmailError = '';
        this.EmailValid = false;
        if (!(this.Email.match(/^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?/))) {
            EmailError = this.rootStore.trialStore.trialData.ErrorEmail;
        } else {
            this.EmailValid = true;
            const wrapper = document.querySelector('#trialFormWrapper'),
                track = wrapper ? wrapper.dataset.abandonedCartProgram : false,
                data = {
                    ProgramName: track,
                    Email: this.Email
                };

            // send out the email
            if (track && (this.Email !== this.EmailChange)) {
                abandonnmentEmail(data);
                this.EmailChange = this.Email;
             }

        }
        this.EmailError = EmailError;
    }

    @action PasswordValidation() {
        this.PasswordValid;
        this.PasswordFocus = true;
        this.Requirement1 = this.Password.length >= 8 && this.Password.length <= 32 ? true : false;
        this.Requirement2 = this.Password.match(/[A-z]/) ? true : false;
        this.Requirement3 = this.Password.match(/\d/) ? true : false;
        this.PasswordValid = this.Requirement1 && this.Requirement2 && this.Requirement3;
        this.PasswordError = !this.Requirement1 || !this.Requirement2 || !this.Requirement3 ? 'Invalid' : '';
    }

    @action RadioValidation() {
        let RadioError = '';
        if (this.CompanySize === '') {
            RadioError = this.rootStore.trialStore.trialData.ErrorCompany;
        }
        this.RadioError = RadioError;
    }
}
